import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  refreshAllRecipients,
  selectCurrentMessage,
} from "../reducers/messagesSlice";

const POLL_INTERVAL = 4000;

const hasAdvanced = (current, baseline) => {
  if (!current) return false;
  if (!baseline) return true;
  return new Date(current).getTime() > new Date(baseline).getTime();
};

export const useRefreshAllRecipients = ({
  tournamentID,
  messageID,
  autoRefresh = false,
}) => {
  const dispatch = useDispatch();
  const currentMessage = useSelector(selectCurrentMessage);

  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const baselineRef = useRef(null);
  const intervalRef = useRef(null);
  const activeRef = useRef(false);
  const refreshedAtRef = useRef(null);
  refreshedAtRef.current = currentMessage?.recipientsRefreshedAt ?? null;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (baseline) => {
      if (!activeRef.current) return;
      baselineRef.current = baseline;
      setRefreshing(true);
      clearTimer();
      intervalRef.current = setInterval(() => {
        dispatch(getMessage({ tournamentID, messageID, recipientStats: true }));
      }, POLL_INTERVAL);
    },
    [dispatch, tournamentID, messageID, clearTimer]
  );

  const trigger = useCallback(
    async (manualRefresh) => {
      const baseline = refreshedAtRef.current;
      if (manualRefresh) setRefreshing(true);
      try {
        const result = await dispatch(
          refreshAllRecipients({ tournamentID, messageID, manualRefresh })
        ).unwrap();
        if (!activeRef.current) return;
        if (result?.recipientsRefreshing === true) {
          startPolling(baseline);
        } else if (manualRefresh) {
          setRefreshing(false);
        }
      } catch {
        if (activeRef.current && manualRefresh) setRefreshing(false);
      } finally {
        if (activeRef.current && !manualRefresh) setLoading(false);
      }
    },
    [dispatch, tournamentID, messageID, startPolling]
  );

  const refresh = useCallback(() => trigger(true), [trigger]);

  useEffect(() => {
    activeRef.current = true;
    if (autoRefresh && tournamentID && messageID) {
      setLoading(true);
      trigger(false);
    }
    return () => {
      activeRef.current = false;
      clearTimer();
      setRefreshing(false);
    };
  }, [autoRefresh, tournamentID, messageID, trigger, clearTimer]);

  useEffect(() => {
    if (!refreshing) return;
    if (
      currentMessage?.recipientsRefreshing === false &&
      hasAdvanced(currentMessage?.recipientsRefreshedAt, baselineRef.current)
    ) {
      clearTimer();
      setRefreshing(false);
    }
  }, [
    refreshing,
    currentMessage?.recipientsRefreshing,
    currentMessage?.recipientsRefreshedAt,
    clearTimer,
  ]);

  return { refreshing, refresh, loading };
};
